/**
 * The account profile group for the main toolbar (HS2-26MSM4): Kerf's avatar
 * `ToolbarControlGroup` paints the user's picture (with a scrim), and a Kerf
 * `PopupMenu` is its single nested trigger and menu (HS2-XH2A8Q) — the official
 * composition, not a hand-written `wa-dropdown`. The menu names who is signed in,
 * then offers Sign out; settings live in the Settings view (HS2-CJM2YK). A user
 * in more than one organization, or invited to another, also gets an
 * Organizations group: choosing one enters it, and choosing a pending invitation
 * accepts it (HS2-QZ4PAD).
 */
import { LucideIcon } from '@kerfjs/ui/lucide-icon';
import { PopupMenu, type PopupMenuEntry } from '@kerfjs/ui/popup-menu';
import { Text } from '@kerfjs/ui/text';
import { ToolbarControlGroup } from '@kerfjs/ui/toolbar-control-group';
import { type SafeHtml } from 'kerfjs';
import { LogOut } from 'lucide';

import { me, orgs, settings } from '../data/state.js';
import { profileAvatar } from '../lib/initials-avatar.js';

/** The organization switcher entries, or none for a user with a single organization. */
function organizationEntries(): PopupMenuEntry[] {
  if (orgs.value.length < 2) return [];
  const entries: PopupMenuEntry[] = [{ kind: 'divider' }, { kind: 'heading', label: 'Organizations' }];
  for (const org of orgs.value) {
    entries.push({
      label: org.name,
      checked: org.current,
      ...(org.current ? {} : { action: 'switch-org', attributes: { 'data-org-id': org.id } }),
      ...(org.status === 'invited'
        ? {
            details: (
              <Text variant="span" tone="quiet" size="compact">
                Invited
              </Text>
            ),
          }
        : {}),
      ...(org.status === 'suspended' && !org.current
        ? { disabled: true, disabledReason: 'Your membership in this organization is suspended.' }
        : {}),
    });
  }
  entries.push({ kind: 'divider' });
  return entries;
}

export function ProfileGroup(): SafeHtml {
  const current = orgs.value.find((o) => o.id === me.value?.organizationId);
  const org = current?.name ?? '';
  const email = me.value?.email ?? '';
  const name =
    (settings.value?.profile as { name?: string } | undefined)?.name ??
    me.value?.name ??
    (email !== '' ? email.split('@')[0] : 'Account');
  // External OIDC pictures cannot load under the app's image CSP; use initials for those too.
  const picture = me.value?.picture ?? '';
  const avatar = profileAvatar(name ?? '', picture);
  // One identity heading: Kerf menu headings are section labels, so the menu
  // names who is signed in and where, without the email or role.
  const identity = [name ?? 'Account', org].filter((part) => part !== '').join(' · ');
  const items: PopupMenuEntry[] = [
    { kind: 'heading', label: identity },
    ...organizationEntries(),
    { label: 'Sign Out', action: 'signout', icon: <LucideIcon icon={LogOut} name="" /> },
  ];
  return (
    <ToolbarControlGroup
      label="Account"
      single={true}
      appearance="contained"
      content="avatar"
      scrim={avatar.scrim}
      avatarImage={avatar.url}
      nestedDropdown={true}
      className="profile-group"
    >
      <PopupMenu
        label="Account Menu"
        caret={false}
        placement="bottom-end"
        items={items}
        rootAttributes={{ 'data-profile-menu': '' }}
      />
    </ToolbarControlGroup>
  );
}
